"use client";

import { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay, FaRulerVertical, FaSolarPanel, FaTemperatureHalf, FaTowerBroadcast } from "react-icons/fa6";
import { LuCpu, LuWind } from "react-icons/lu";
import { Footer } from "@/app/components/Footer";
import { Image } from "@/app/components/Image";
import NavbarSnowsense from "@/app/components/NavbarSnowsense";
import { ScrollReveal } from "@/app/components/ScrollReveal";
import { ArrowIcon, Container, Pill, RuleBlock, Section, SitePage } from "@/app/components/SiteUi";
import { useI18n } from "@/lib/i18n";

const components = [
	{ key: "about_sm4.item_cable", Icon: FaTemperatureHalf, lastAsNote: true },
	{ key: "about_sm4.item_laser", Icon: FaRulerVertical },
	{ key: "about_sm4.item_wind", Icon: LuWind },
	{ key: "about_sm4.item_logger", Icon: LuCpu },
	{ key: "about_sm4.item_comm", Icon: FaTowerBroadcast },
	{ key: "about_sm4.item_solar", Icon: FaSolarPanel },
];

const items = ["snow_page.item_air", "snow_page.item_profile"];
const subItems = ["snow_page.sub_item_refrozen", "snow_page.sub_item_wet", "snow_page.sub_item_gradient"];

// split text into n paragraphs at sentence ends, abbreviations like "t.d." stay intact
const sentenceEnd = /\.\s+(?=[A-ZÁÉÍÓÚÝÞÆÖ])/g;
const toSentences = text => text.replace(sentenceEnd, ".\n").split("\n");

function splitSentences(text, n) {
	const sentences = toSentences(text);
	const per = Math.ceil(sentences.length / n);
	const paras = [];
	for (let i = 0; i < sentences.length; i += per) paras.push(sentences.slice(i, i + per).join(" "));
	return paras;
}

export default function AboutSm4() {
	const { t } = useI18n();
	const introParas = splitSentences(t("snow_page.intro"), 3);
	const videoRef = useRef(null);
	const [playing, setPlaying] = useState(true);
	const [showToggle, setShowToggle] = useState(false);

	// the pause button is only shown to people who ask for reduced motion, or when the
	// browser blocks autoplay (iOS low power mode) so the video can still be started
	useEffect(() => {
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) setShowToggle(true);
		videoRef.current?.play().catch(() => {
			setPlaying(false);
			setShowToggle(true);
		});
	}, []);

	const toggleVideo = () => {
		const video = videoRef.current;
		if (!video) return;
		if (video.paused) {
			video
				.play()
				.then(() => setPlaying(true))
				.catch(() => {});
		} else {
			video.pause();
			setPlaying(false);
		}
	};

	return (
		<SitePage>
			<NavbarSnowsense />

			<main>
				<Section theme="photo" className="flex min-h-screen flex-col">
					<div className="relative min-h-[clamp(340px,54vh,600px)] flex-1 overflow-hidden">
						<video
							ref={videoRef}
							className="absolute inset-0 h-full w-full object-cover object-[center_bottom]"
							aria-hidden="true"
							poster="/pictures/new/svn_video_poster.jpg"
							autoPlay
							loop
							muted
							playsInline
						>
							<source src="/pictures/new/svn_video_web.mp4" type="video/mp4" />
						</video>
						<div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,.45)_0%,rgba(0,0,0,0)_35%,rgba(0,0,0,0)_60%,rgba(0,0,0,.55)_100%)]" />
						{showToggle && (
							<button
								type="button"
								onClick={toggleVideo}
								aria-label={playing ? "Pause video" : "Play video"}
								className="absolute bottom-[clamp(20px,4vh,36px)] right-[clamp(20px,4vw,48px)] z-10 flex size-10 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur-sm transition-colors hover:bg-black/50"
							>
								{playing ? (
									<FaPause size={13} aria-hidden />
								) : (
									<FaPlay size={13} aria-hidden className="ml-0.5" />
								)}
							</button>
						)}
						<div className="absolute inset-x-0 bottom-[clamp(20px,4vh,36px)]">
							<Container>
								<h1 className="text-[clamp(32px,4vw,56px)] font-bold leading-none tracking-[-0.02em] text-white [text-shadow:0_2px_24px_rgba(0,0,0,.35)]">
									{t("about_sm4.page_title")}
									<span
										aria-hidden="true"
										className="mt-3 block h-[3px] w-16 rounded-full bg-white/80"
									/>
								</h1>
							</Container>
						</div>
					</div>

					<div data-nav="tint" className="bg-tint text-gray-900">
						<Container className="py-[clamp(32px,5vh,56px)]">
							<div className="mx-auto max-w-[1040px]">
								<h2 className="mb-3 text-[clamp(24px,2vw,30px)] font-bold leading-tight">
									{t("about_sm4.heading")}
								</h2>
								<RuleBlock className="max-w-[820px]">
									<p className="text-[clamp(18px,1.5vw,21px)] leading-normal">
										{t("about_sm4.intro")}
									</p>
								</RuleBlock>
								<ul className="mt-6 grid grid-cols-3 gap-x-8 gap-y-4 max-[1100px]:grid-cols-2 max-nav:grid-cols-1">
									{components.map(({ key, Icon, lastAsNote }) => {
										const sentences = toSentences(t(key));
										const note = lastAsNote && sentences.length > 1 ? sentences.pop() : null;
										return (
											<li key={key} className="grid grid-cols-[36px_1fr] items-start gap-3">
												<span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-white">
													<Icon size={16} aria-hidden />
												</span>
												<p className="text-[15px] leading-[1.45] text-gray-600">
													{sentences.join(" ")}
													{note && (
														<span className="mt-1.5 block text-[13px] leading-[1.45] text-gray-500 before:content-['–_']">
															{note}
														</span>
													)}
												</p>
											</li>
										);
									})}
								</ul>
								<p className="mt-4 text-center text-[14px] text-gray-500">{t("about_sm4.outro")}</p>
							</div>
						</Container>
					</div>
				</Section>

				<Section theme="light" className="pb-[clamp(32px,5vh,56px)] pt-[clamp(72px,10vh,120px)]">
					<Container>
						<figure className="mx-auto w-full max-w-[1120px]">
							<ScrollReveal className="grid grid-cols-2 gap-6 max-nav:gap-3">
								<Image
									src="/pictures/skyrimynd.jpg"
									alt="SM4 sensor unit with its temperature cable"
									className="h-auto w-full rounded-lg shadow-[0_16px_40px_rgba(41,51,92,.12)]"
								/>
								<Image
									src="/pictures/isl5.jpeg"
									alt="Top of an SM4 mast with the laser sensor, on a mountain ridge"
									className="h-auto w-full rounded-lg shadow-[0_16px_40px_rgba(41,51,92,.12)]"
								/>
							</ScrollReveal>
							<figcaption className="mt-4 text-center text-[16px] italic text-gray-500">
								{t("about_sm4.caption")}
							</figcaption>
						</figure>
						<p className="mx-auto mt-[clamp(40px,6vh,72px)] max-w-[60ch] text-center text-[clamp(20px,1.6vw,24px)] font-semibold leading-[1.45] text-gray-900">
							{introParas[0]}
						</p>
						<RuleBlock className="mt-[clamp(28px,4vh,44px)] max-w-none">
							<div className="nav:columns-2 nav:gap-x-[clamp(40px,5vw,80px)] [&>ul]:break-inside-avoid">
								{introParas.slice(1).map(para => (
									<p key={para} className="mb-5 text-[18px] leading-[1.7] text-gray-700">
										{para}
									</p>
								))}
								<ul className="mb-5 flex flex-col gap-2 text-[18px] leading-[1.7] text-gray-700">
									{items.map(key => (
										<li
											key={key}
											className="relative pl-5 before:absolute before:left-0 before:top-[0.65em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-primary-500"
										>
											{t(key)}
										</li>
									))}
								</ul>
								{subItems.map((sub, i) => (
									<p
										key={sub}
										className={
											i === subItems.length - 1
												? "mb-0 text-[18px] leading-[1.7] text-gray-700"
												: "mb-5 text-[18px] leading-[1.7] text-gray-700"
										}
									>
										{t(sub)}
									</p>
								))}
							</div>
						</RuleBlock>
					</Container>
				</Section>

				<Section theme="light" className="pb-[clamp(56px,8vh,96px)] pt-[clamp(24px,4vh,48px)]">
					<Container className="flex flex-col items-center gap-6 text-center">
						<p className="mx-auto max-w-[56ch] text-center text-[clamp(20px,1.7vw,24px)] leading-normal text-balance">
							{t("snow_page.outro")}
						</p>
						<div className="flex flex-wrap justify-center gap-3">
							<Pill variant="primary" href="/snowsense/charts">
								{t("navbar_about_snowsense.read_charts")}
								<ArrowIcon />
							</Pill>
							<Pill variant="outline" href="/snowsense/products">
								{t("navbar_about_snowsense.products")}
							</Pill>
						</div>
					</Container>
				</Section>
			</main>

			<Footer />
		</SitePage>
	);
}
